/**
 * Métricas agregadas de la vista de People (rediseño 04/09 según mockup).
 *
 * Se recalculan sobre las filas FILTRADAS con la misma función que usa el
 * backend (`shared/dtalk-stats`), así que cruzar por área / país / líder /
 * nivel actualiza los números al instante y sin pedirle nada a la API.
 * Nunca muestra contenido conversacional: solo conteos.
 */
import { LicenseCard } from '@/components/performance/LicenseCard';
import { StatCard } from '@/components/ui/stat-card';
import { Card, CardContent } from '@/components/ui/card';
import { Users, CheckCircle2, Clock, AlertTriangle, HelpCircle } from 'lucide-react';
import {
  buildDTalkPeopleStats,
  DTALK_CATEGORIA_LABEL,
  type DTalkStatsRow,
  type DTalkCategoriaKey,
} from '@/../../shared/dtalk-stats';
import { DonutChart } from '@/pages/performance/dtalk/DonutChart';
import { DTalkStatusBars } from './DTalkStatusBars';
import { useTranslation } from 'react-i18next';


const CAT_COLOR: Record<string, string> = {
  promocion: '#8b5cf6',            // violet-500
  movimiento_horizontal: '#0ea5e9', // sky-500
  sin_claridad: '#f59e0b',          // amber-500
};

export function DTalkPeopleMetrics({
  rows,
  onOpenLicenses,
  canEditLicense,
}: {
  rows: DTalkStatsRow[];
  /** Abre el gestor de licencias (dialog con búsqueda + marcar/reactivar). */
  onOpenLicenses?: () => void;
  canEditLicense?: boolean;
}) {
  const { t } = useTranslation();
  const s = buildDTalkPeopleStats(rows);
  const pctLicencia = s.totalPersonas > 0 ? Math.round((s.enLicencia / s.totalPersonas) * 1000) / 10 : 0;

  return (
    <div className="space-y-4">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard tone="default"
          icon={Users}
          label="Total Clappers"
          value={String(s.totalPersonas)}
          sub={`${s.convocadas} convocadas · ${s.enLicencia} en licencia · ${s.sinAntiguedad} con menos de 3 meses`}
        />
        <StatCard
          icon={CheckCircle2}
          label="Conversaciones completadas"
          value={`${s.pctCompletadas}%`}
          sub={`${s.estado.completadas} de ${s.convocadas} convocadas`}
          tone={s.pctCompletadas >= 70 ? 'good' : s.pctCompletadas >= 40 ? 'warn' : 'default'}
        />
        <StatCard tone="default"
          icon={Clock}
          label="En curso"
          value={String(s.estado.enCurso)}
          sub={`${s.estado.noIniciadas} todavía no empezaron`}
        />
        <StatCard
          icon={AlertTriangle}
          label="Con alerta"
          value={String(s.alerta.personas)}
          sub={s.alerta.personas > 0 ? 'Requieren seguimiento de People' : 'Sin alertas registradas'}
          tone={s.alerta.personas > 0 ? 'bad' : 'good'}
        />
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        {/* Distribución por categoría de carrera (enum del agente — única taxonomía) */}
        <Card>
          <CardContent className="py-4">
            <h3 className="mb-3 text-sm font-semibold">{t('performance.dtalkpeoplemetrics.distribucion_por_categoria_de_carrera')}</h3>
            {s.categorias.length === 0 ? (
              <p className="py-4 text-center text-sm text-muted-foreground">
                {t('performance.dtalkpeoplemetrics.todavia_no_hay_conversaciones_cerradas_con')}
              </p>
            ) : (
              <div className="flex flex-col sm:flex-row items-center gap-5">
                <DonutChart
                  data={[
                    ...s.categorias.map((c) => ({
                      label: DTALK_CATEGORIA_LABEL[c.key as DTalkCategoriaKey] ?? c.key,
                      value: c.count,
                      color: CAT_COLOR[c.key] ?? '#94a3b8',
                    })),
                    { label: t('performance.dtalkpeoplemetrics.sin_categoria'), value: s.sinCategoria, color: '#e2e8f0' },
                  ]}
                  centerValue={s.convocadas}
                  centerLabel="Clappers"
                />
                <div className="flex-1 w-full space-y-2 text-sm">
                  {s.categorias.map((c) => (
                    <div key={c.key} className="flex items-baseline justify-between gap-2">
                      <span className="inline-flex items-center gap-1.5 min-w-0 truncate">
                        <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ backgroundColor: CAT_COLOR[c.key] ?? '#94a3b8' }} />
                        {DTALK_CATEGORIA_LABEL[c.key as DTalkCategoriaKey] ?? c.key}
                      </span>
                      <span className="text-xs text-muted-foreground shrink-0">{c.count} ({c.pct}%)</span>
                    </div>
                  ))}
                  {s.sinCategoria > 0 && (
                    <p className="flex items-center gap-1 pt-1 text-xs text-muted-foreground">
                      <HelpCircle className="h-3 w-3 shrink-0" />
                      {t('performance.dtalkpeoplemetrics.sin_categoria_todavia', { n: s.sinCategoria })}
                    </p>
                  )}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Estado de la conversación (mockup) */}
        <DTalkStatusBars rows={rows} />
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        {/* Aspiraciones secundarias */}
        <Card>
          <CardContent className="py-4">
            <h3 className="mb-3 text-sm font-semibold">{t('performance.dtalkpeoplemetrics.aspiraciones_secundarias')}</h3>
            <div className="space-y-1.5 text-sm">
              <div className="flex items-baseline justify-between">
                <span className="text-muted-foreground">{t('performance.dtalkpeoplemetrics.con_aspiracion_secundaria')}</span>
                <span><b>{s.secundarias.personas}</b> <span className="text-xs text-muted-foreground">({s.convocadas > 0 ? Math.round((s.secundarias.personas / s.convocadas) * 100) : 0}%)</span></span>
              </div>
              <div className="flex items-baseline justify-between">
                <span className="text-muted-foreground">{t('performance.dtalkpeoplemetrics.sin_aspiracion_secundaria')}</span>
                <span><b>{Math.max(0, s.convocadas - s.secundarias.personas)}</b></span>
              </div>
              {s.secundarias.porCategoria.length > 0 && (
                <p className="pt-1 text-xs text-muted-foreground">
                  {s.secundarias.porCategoria
                    .map((c) => `${DTALK_CATEGORIA_LABEL[c.key as DTalkCategoriaKey] ?? c.key}: ${c.count}`)
                    .join(' · ')}
                </p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Alertas (dimensión separada de la aspiración) */}
        <Card>
          <CardContent className="py-4">
            <h3 className="mb-3 text-sm font-semibold">{t('performance.dtalkpeoplemetrics.personas_con_alerta')}</h3>
            {s.alerta.personas === 0 ? (
              <p className="text-sm text-muted-foreground">{t('performance.dtalkpeoplemetrics.sin_alertas_registradas')}</p>
            ) : (
              <div className="text-sm">
                <p className="mb-1"><b>{s.alerta.personas}</b> {t('performance.dtalkpeoplemetrics.requieren_seguimiento')}</p>
                <ul className="space-y-0.5 text-xs text-muted-foreground">
                  {s.alerta.motivos.map((m) => (
                    <li key={m.key} className="truncate">• {m.key} ({m.count})</li>
                  ))}
                </ul>
              </div>
            )}
          </CardContent>
        </Card>

        <LicenseCard
          count={s.enLicencia}
          pct={pctLicencia}
          hint={t('performance.dtalkpeoplemetrics.no_se_les_pide_la_conversacion')}
          onManage={canEditLicense && onOpenLicenses ? onOpenLicenses : undefined}
        />
      </div>
    </div>
  );
}
